import { APP_BASE_HREF, CommonModule, DOCUMENT } from "@angular/common";
import { HttpClientModule } from "@angular/common/http";
import { NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { EffectsModule } from "@ngrx/effects";
import { routerReducer, StoreRouterConnectingModule } from "@ngrx/router-store";
import { StoreModule } from "@ngrx/store";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { AccountCamMicDataAccessModule } from "@youm-front/account-cammic/data-access";
import { AccountCammicFeatureAccountCammicModule } from "@youm-front/account-cammic/feature/account-cammic";
import { AccountDeviceDataAccessModule } from "@youm-front/account-device/data-access";
import {
  AccountJwtDataAccessModule,
  ACCOUNT_JWT_CONFIG
} from "@youm-front/account-jwt/data-access";
import { AccountDataAccessModule } from "@youm-front/account/data-access";
import { ApiDataAccessModule } from "@youm-front/api/data-access";
import {
  BRAND_BG_MAX_COUNT_TOKEN,
  BRAND_CAPTION_LOWER_THIRD_MAX_COUNT_TOKEN,
  BRAND_LOGO_MAX_COUNT_TOKEN,
  BRAND_MAX_COUNT_TOKEN,
  BRAND_OVERLAY_MAX_COUNT_TOKEN
} from "@youm-front/brand/data-access";
import { Conf2DataAccessModule } from "@youm-front/conf2/data-access";
import { ConferenceStreamDataAccessModule } from "@youm-front/conference-stream/data-access";
import { CoreModule, IconsModule, YoumAPIModule } from "@youm-front/core";
import { DestinationsDataAccessModule } from "@youm-front/destinations/data-access";
import { DevToolsDataAccessModule } from "@youm-front/dev-tools/data-access";
import { DeviceDataAccessModule } from "@youm-front/device/data-access";
// TODO:
// eslint-disable-next-line @nx/enforce-module-boundaries
import { MicVolumeModule } from "@youm-front/feature/mic-volume";
import {
  LokiDataAccessModule,
  LOKI_CONFIG
} from "@youm-front/loki/data-access";
import { NoticeDataAccessModule } from "@youm-front/notice/data-access";
import { NoticeFeatureNoticeModule } from "@youm-front/notice/feature/notice";
import {
  RoomDataAccessModule,
  RoomStudioDataAccessModule
} from "@youm-front/room/data-access";
import { ROOM_APP_URL, ROOM_SIGN_IN_URL } from "@youm-front/room/provider";
import { SettingsFeatureSettingsModule } from "@youm-front/settings/feature/settings";
import { TelemetryDataAccessModule } from "@youm-front/telemetry/data-access";
import { UiPopoverModule } from "@youm-front/ui-popover";
import { UiSvgModule } from "@youm-front/ui-svg";
import { ErrorHandlerModule } from "@youm-front/utils/error-handler";
import { runtimeEnvironment } from "../runtime-environments/runtime-environment";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { Conf2SignInFailureComponent } from "./conf2-sign-in-failure/conf2-sign-in-failure.component";
import Guards from "./guards";
import { RoomStoreModule } from "./stores";

@NgModule({
  declarations: [AppComponent, Conf2SignInFailureComponent],
  imports: [
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    StoreModule.forRoot({
      router: routerReducer
    }),
    StoreRouterConnectingModule.forRoot(),
    EffectsModule.forRoot([]),
    StoreDevtoolsModule.instrument({
      maxAge: 25
    }),
    AccountJwtDataAccessModule,
    AccountCamMicDataAccessModule,
    AccountCammicFeatureAccountCammicModule,
    AccountDataAccessModule.forRoot({
      noAuthRedirectToUrl: runtimeEnvironment.noAuthRedirectToURL
    }),
    AccountDeviceDataAccessModule,
    ApiDataAccessModule.forRoot({
      serverUrl: runtimeEnvironment.serverURL
    }),
    CoreModule.forRoot({
      environment: {
        serverURL: runtimeEnvironment.serverURL,
        domain: runtimeEnvironment.domain
      }
    }),
    DeviceDataAccessModule,
    DevToolsDataAccessModule,
    ErrorHandlerModule,
    IconsModule,
    LokiDataAccessModule,
    MicVolumeModule,
    NoticeDataAccessModule,
    NoticeFeatureNoticeModule,
    RoomDataAccessModule,
    RoomStudioDataAccessModule,
    RoomStoreModule,
    SettingsFeatureSettingsModule,
    UiPopoverModule,
    UiSvgModule,
    YoumAPIModule.forRoot({
      url: runtimeEnvironment.socketURL
    }),
    Conf2DataAccessModule,
    DestinationsDataAccessModule,
    ConferenceStreamDataAccessModule,
    TelemetryDataAccessModule,
    AppRoutingModule
  ],
  providers: [
    ...Guards,
    {
      provide: ACCOUNT_JWT_CONFIG,
      useValue: {
        domain: runtimeEnvironment.domain,
        name: runtimeEnvironment.tokenName
      }
    },
    {
      provide: APP_BASE_HREF,
      deps: [DOCUMENT],
      useFactory: (document: Document) => document.location.origin
    },
    {
      provide: BRAND_MAX_COUNT_TOKEN,
      useValue: runtimeEnvironment.brandMaxCount
    },
    {
      provide: BRAND_BG_MAX_COUNT_TOKEN,
      useValue: runtimeEnvironment.brandBgMaxCount
    },
    {
      provide: BRAND_LOGO_MAX_COUNT_TOKEN,
      useValue: runtimeEnvironment.brandLogoMaxCount
    },
    {
      provide: BRAND_OVERLAY_MAX_COUNT_TOKEN,
      useValue: runtimeEnvironment.brandOverlayMaxCount
    },
    {
      provide: BRAND_CAPTION_LOWER_THIRD_MAX_COUNT_TOKEN,
      useValue: runtimeEnvironment.brandCaptionLowerThirdMaxCount
    },
    {
      provide: LOKI_CONFIG,
      useValue: { container: "youm-studio-front" }
    },
    {
      provide: ROOM_APP_URL,
      useValue: runtimeEnvironment.appURL
    },
    {
      provide: ROOM_SIGN_IN_URL,
      useValue: runtimeEnvironment.noAuthRedirectToURL
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
